import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Copy, Info } from 'lucide-react';
import { api } from '../api/cliente.js';
import { Pill } from '../componentes/ui/Pill.js';

interface Dados {
  nameservers: { hostname: string; ip: string }[];
  zonas: { zona: string; bloco: string; delegada: boolean }[];
}

/**
 * Instruções de delegação.
 *
 * É a tela que evita mais chamado: o cliente precisa informar os nameservers no
 * RIR, e essa informação normalmente circula por e-mail e se perde. Aqui ela
 * fica onde ele já está, com botão de copiar.
 */
export function Delegacao() {
  const [copiado, setCopiado] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['portal', 'delegacao'],
    queryFn: () => api.get<Dados>('/portal/delegacao'),
  });

  if (isLoading || !data) return <Esqueleto />;

  const copiar = (texto: string, chave: string) => {
    void navigator.clipboard.writeText(texto);
    setCopiado(chave);
    setTimeout(() => setCopiado(null), 2000);
  };

  const todos = data.nameservers.map((n) => n.hostname).join('\n');

  return (
    <div className="space-y-5">
      <section className="rounded-card border border-borda bg-superficie p-5">
        <h2 className="text-[15px] font-extrabold">Nameservers</h2>
        <p className="mt-1 text-[13px] text-texto-suave">
          Informe estes servidores como responsáveis pelo DNS reverso dos seus blocos, no painel
          do RIR onde eles estão registrados (Registro.br, LACNIC, ARIN…).
        </p>

        <ul className="mt-4 space-y-2">
          {data.nameservers.map((n) => (
            <li
              key={n.hostname}
              className="flex flex-wrap items-center gap-3 rounded-chip bg-fundo px-3 py-2.5"
            >
              <span className="flex-1 font-mono text-[13.5px] font-semibold">{n.hostname}</span>
              <span className="font-mono text-[12px] text-texto-suave">{n.ip}</span>
              <button
                onClick={() => copiar(n.hostname, n.hostname)}
                className="flex items-center gap-1 rounded-chip border border-borda bg-superficie px-2 py-1 text-[11.5px] font-semibold text-texto-medio hover:border-marca hover:text-marca"
              >
                <Copy size={12} />
                {copiado === n.hostname ? 'copiado' : 'copiar'}
              </button>
            </li>
          ))}
        </ul>

        <button
          onClick={() => copiar(todos, 'todos')}
          className="mt-3 text-[12.5px] font-semibold text-marca hover:underline"
        >
          {copiado === 'todos' ? 'copiados!' : 'copiar todos os nameservers'}
        </button>
      </section>

      <section>
        <h2 className="mb-2 text-[15px] font-extrabold">Zonas reversas dos seus blocos</h2>
        <p className="mb-3 text-[13px] text-texto-suave">
          Cada zona precisa ser delegada separadamente. Um bloco maior que /24 gera mais de uma.
        </p>

        <div className="overflow-hidden rounded-card border border-borda bg-superficie">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr className="border-b border-borda bg-borda-tenue">
                  {['Zona reversa', 'Bloco', 'Situação', ''].map((h) => (
                    <th
                      key={h}
                      className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.zonas.map((z) => (
                  <tr key={z.zona} className="border-t border-borda-suave">
                    <td className="px-[18px] py-[13px] font-mono text-[13px]">{z.zona}</td>
                    <td className="px-[18px] py-[13px] font-mono text-[12.5px] text-texto-suave">
                      {z.bloco}
                    </td>
                    <td className="px-[18px] py-[13px]">
                      {z.delegada ? (
                        <Pill tom="ok">
                          <CheckCircle2 size={12} className="mr-1" /> Delegada
                        </Pill>
                      ) : (
                        <Pill tom="aviso">Aguardando delegação</Pill>
                      )}
                    </td>
                    <td className="px-[18px] py-[13px] text-right">
                      <button
                        onClick={() => copiar(z.zona, z.zona)}
                        className="text-[12px] font-semibold text-marca hover:underline"
                      >
                        {copiado === z.zona ? 'copiado' : 'copiar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="flex items-start gap-3 rounded-card border border-info/20 bg-info-fundo px-4 py-4 text-info">
        <Info size={18} className="mt-0.5 shrink-0" />
        <div className="text-[13px] leading-relaxed">
          <p className="font-extrabold">Por que a delegação é necessária</p>
          <p className="mt-1 opacity-90">
            Os reversos configurados no portal ficam nos nossos servidores. Enquanto o RIR não
            apontar a zona para eles, o restante da internet continua perguntando ao servidor
            antigo — e o reverso parece não ter sido aplicado, mesmo estando correto aqui.
          </p>
        </div>
      </section>
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="space-y-5">
      <div className="h-56 animate-pulse rounded-card border border-borda bg-superficie" />
      <div className="h-48 animate-pulse rounded-card border border-borda bg-superficie" />
    </div>
  );
}
